import { Controller } from "@hotwired/stimulus"
import {
  extractClipboard,
  countTopLevelElements,
  countTextChunks,
  importPastedContent
} from "../modules/panel/smart_paste"

const CAPTURED_PLACEHOLDER = "Content captured — paste again to replace it"
const UNCERTAIN_IMPORT_MESSAGE = "The import may still have gone through — reloading to check…"
const RELOAD_DELAY_MS = 1500

// Modal widget that captures a Word/HTML paste and imports it as content
// blocks via the import_html endpoint.
export default class extends Controller {
  static targets = ["overlay", "zone", "status", "error", "importButton", "importLabel"]
  static values = { url: String }

  connect() {
    this.resetForCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.resetForCache)

    this.keyDownHandler = (event) => {
      if (event.key === "Escape" && this.isOpen()) this.close()
    }
    document.addEventListener("keydown", this.keyDownHandler)

    if (this.hasZoneTarget) {
      this.defaultPlaceholder = this.zoneTarget.dataset.placeholder
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.resetForCache)
    document.removeEventListener("keydown", this.keyDownHandler)
  }

  open(event) {
    event?.preventDefault()
    this.overlayTarget.classList.remove("hidden")
    this.reset()
    this.zoneTarget.focus()
  }

  close(event) {
    event?.preventDefault()
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
    this.reset()
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  capture(event) {
    event.preventDefault()
    // Stamp a token once per capture so a retry after a client timeout re-sends
    // the same key and the server dedups it rather than duplicating blocks.
    this.captured = { ...extractClipboard(event.clipboardData), token: crypto.randomUUID() }
    const count = this.captured.html
      ? countTopLevelElements(this.captured.html)
      : countTextChunks(this.captured.text)

    if (count === 0) {
      this.captured = null
      this.showError("Nothing usable was found in the paste. Try copying the content again.")
      return
    }

    this.showStatus(`Content captured — ready to import about ${count} block${count === 1 ? "" : "s"}.`)
    this.importButtonTarget.classList.remove("hidden")
    this.importLabelTarget.textContent = `Import ${count} block${count === 1 ? "" : "s"}`
    this.zoneTarget.dataset.placeholder = CAPTURED_PLACEHOLDER
  }

  async import(event) {
    event?.preventDefault()
    if (!this.captured || this.importing) return

    this.importing = true
    this.importButtonTarget.disabled = true
    const result = await importPastedContent(this.urlValue, this.captured)
    this.importing = false

    if (!result.error) {
      window.location.reload()
      return
    }

    if (result.retriable) {
      this.importButtonTarget.disabled = false
      this.showError(result.error, { keepImportButton: true })
    } else {
      this.showError(UNCERTAIN_IMPORT_MESSAGE)
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS)
    }
  }

  reset() {
    this.captured = null
    this.importing = false
    this.zoneTarget.innerHTML = ""
    if (this.defaultPlaceholder != null) {
      this.zoneTarget.dataset.placeholder = this.defaultPlaceholder
    }
    this.statusTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.importButtonTarget.classList.add("hidden")
    this.importButtonTarget.disabled = false
  }

  showStatus(message) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
    this.errorTarget.classList.add("hidden")
  }

  showError(message, { keepImportButton = false } = {}) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
    this.statusTarget.classList.add("hidden")
    if (!keepImportButton) this.importButtonTarget.classList.add("hidden")
  }

  isOpen() {
    return this.hasOverlayTarget && !this.overlayTarget.classList.contains("hidden")
  }
}
